import Gallery from '@website/components/directoryTheme/Gallery';
import {
  Media,
  Review,
  TripsPageQuery,
} from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import Link from 'next/link';
import Avatar from '../../../directoryTheme/Avatar';
import Stars from '../../../directoryTheme/Stars';
import Truncate from '../../../utilities/Truncate';
import ShowReply from './ShowReply';

/**
 * Single Review Card
 */
export default function CardReview({
  review,
  host,
  trip,
}: {
  review: Pick<Review, 'id' | 'startDate' | 'fiveStarRating' | 'body'> & {
    user: TripsPageQuery['tripByURL']['reviews']['items'][number]['user'];
    media?: { items: Pick<Media, 'id' | 'file'>[] };
  };
  host?: Pick<TripsPageQuery['tripByURL']['host'], 'id' | 'name'>;
  trip?: Pick<TripsPageQuery['tripByURL'], 'urlSlug' | 'title'>;
}) {
  const imagesCount: number = review?.media?.items?.length || 0;

  return (
    <div className="review py-3 px-3">
      {/* Header (name, avatar, stars) */}
      <div className="d-flex flex-row mb-1">
        <div className="text-md-center me-2 me-xl-3">
          <Avatar
            size="md"
            image={review?.user?.picture?.file?.key}
            defaultImageText={review.user?.givenName}
            alt={`${review.user?.givenName}`}
          />
        </div>

        <div style={{ flexGrow: 1 }}>
          <div className="w-full d-flex">
            <h6 className="m-0 d-inline-block mt-1">
              {review.user?.givenName}
            </h6>
            <p className="m-0 d-inline-block ms-1 flex-1">
              ({DateTime.fromISO(review.startDate).toFormat('MMM yyyy')})
            </p>
          </div>
          <div className="mb-2">
            <Stars
              size="sm"
              color="text-primary"
              stars={review.fiveStarRating}
            />
          </div>
        </div>
      </div>
      {/* Text */}
      <Truncate lines={3} enableExpand className="text-muted text-sm">
        {review.body}
      </Truncate>
      {/* Images */}
      {imagesCount > 0 && (
        <Gallery
          images={review.media.items.slice(0, 6)}
          allImages={review.media.items}
          xs={6}
          sm={4}
          md={3}
          lg={2}
          singleRowOnly
        />
      )}
      {host && <ShowReply review={review} host={host} />}

      {trip && (
        <p className="ms-auto text-muted text-sm py-2">
          <span className="fw-bold">Travelled on:</span>{' '}
          <Link target="_blank" href={`/trips/${trip.urlSlug}`}>
            {trip.title}
          </Link>
        </p>
      )}
    </div>
  );
}
